import { useRef } from 'react'
import { css, useTheme, FableTheme } from '@fable/theme'
import isEmpty from 'lodash/isEmpty'

import { FlexBox } from '@fable/components'
import { AudioPlayerInfo } from './AudioPlayerInfo'
import { useAudioPlayer } from './useAudioPlayer'
import { AudioPlayerButton } from './AudioPlayerButton'
import { AudioPlayerIndicator } from './AudioPlayerIndicator'

interface Props {
  className?: string
  source: string
  title: string
  subtitle?: string
  active: boolean
  simple: boolean
}

const AudioPlayer: React.VFC<Props> = ({
  className,
  active,
  simple,
  source,
  subtitle,
  title,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const {
    playing,
    loaded,
    progress,
    currentDuration,
    startingDuration,
    unsupported,
    play,
    pause,
  } = useAudioPlayer(audioRef, active, source)
  const theme = useTheme()
  const styles = getStyles(theme, simple)

  const renderPlayer = () => {
    if (unsupported) return null

    return (
      <div className={className}>
        <audio ref={audioRef} id="audio-player"></audio>
        <div className={styles.control}>
          <FlexBox className={styles.container} alignItems="center">
            <AudioPlayerButton
              loaded={loaded}
              playing={playing}
              play={play}
              pause={pause}
            />
            <AudioPlayerInfo
              title={title}
              subtitle={subtitle}
              loaded={loaded}
              simple={simple}
            />
          </FlexBox>
          {!simple && (
            <AudioPlayerIndicator
              loaded={loaded}
              progress={progress}
              timer={
                !isEmpty(currentDuration) ? currentDuration : startingDuration
              }
            />
          )}
        </div>
      </div>
    )
  }

  return renderPlayer()
}

export default AudioPlayer

const getStyles = ({ colors }: FableTheme, simple: boolean) => ({
  control: css`
    padding: ${simple ? 0 : 16}px;
    padding-top: 20px;
    border-radius: 4px;
    border: ${simple ? 'none' : `1px solid ${colors.whiteSmoke}`};
  `,
  container: css`
    margin-bottom: ${simple ? 0 : 8}px;
    justify-content: ${simple && 'center'};
  `,
})
